import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BadgeCheck, X } from 'lucide-react';
const NAMES = ['Dr. Martín G.', 'Dra. Laura P.', 'Dr. Sebastián R.', 'Dra. Carolina M.', 'Dr. Federico L.', 'Dra. Valeria S.', 'Dr. Joaquín D.', 'Dra. Florencia A.', 'Dr. Nicolás T.', 'Dra. Agustina B.', 'Dr. Lucas F.', 'Dra. Mariana C.', 'Dr. Gonzalo V.', 'Dra. Cecilia H.', 'Dr. Andrés N.', 'Dra. Sofía E.', 'Dr. Ramiro O.', 'Dra. Daniela Q.', 'Dr. Emiliano Z.', 'Dra. Julieta I.'];
const CITIES = ['CABA, Buenos Aires', 'Córdoba', 'Rosario, Santa Fe', 'Mendoza', 'La Plata, Buenos Aires', 'Mar del Plata, Buenos Aires', 'San Miguel de Tucumán', 'Salta', 'Santa Fe', 'Neuquén', 'Bahía Blanca, Buenos Aires', 'Resistencia, Chaco', 'Posadas, Misiones', 'Paraná, Entre Ríos', 'San Juan', 'Corrientes', 'Santiago del Estero', 'San Salvador de Jujuy', 'Río Cuarto, Córdoba', 'Bariloche, Río Negro'];
const OFFERS = ['el Megapack Completo', 'el Kit Esencial'];
const TIME_AGO = ['hace instantes', 'hace 1 minuto', 'hace 2 minutos', 'recién'];
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
interface Notification {
  id: number;
  name: string;
  city: string;
  offer: string;
  ago: string;
}
function buildNotification(): Notification {
  return {
    id: Date.now() + Math.random(),
    name: pick(NAMES),
    city: pick(CITIES),
    offer: pick(OFFERS),
    ago: pick(TIME_AGO)
  };
}
const VISIBLE_MS = 6000; // how long each popup stays on screen
const MIN_GAP_MS = 40000; // 40s
const MAX_GAP_MS = 60000; // 60s
const INITIAL_DELAY_MS = 9000; // first popup after page load
export function PurchasePopup() {
  const [current, setCurrent] = useState<Notification | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const showNext = useCallback(() => {
    setCurrent(buildNotification());
  }, []);
  useEffect(() => {
    if (dismissed) return;
    let hideTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    // Schedule the popup AFTER the current one is hidden
    const scheduleCycle = (delay: number) => {
      nextTimer = setTimeout(() => {
        showNext();
        hideTimer = setTimeout(() => {
          setCurrent(null);
          const gap = MIN_GAP_MS + Math.random() * (MAX_GAP_MS - MIN_GAP_MS);
          scheduleCycle(gap);
        }, VISIBLE_MS);
      }, delay);
    };
    scheduleCycle(INITIAL_DELAY_MS);
    return () => {
      clearTimeout(hideTimer);
      clearTimeout(nextTimer);
    };
  }, [dismissed, showNext]);
  const handleDismiss = () => {
    setCurrent(null);
    setDismissed(true);
  };
  return <div className="fixed bottom-4 left-4 z-50 max-w-[calc(100vw-2rem)] sm:max-w-sm pointer-events-none" aria-live="polite" aria-atomic="true" data-id="element-235">
      <AnimatePresence data-id="element-236">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        y: 24,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.95
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto flex items-start gap-3 bg-white rounded-2xl shadow-2xl border border-slate-200 p-3 pr-9 relative" role="status" data-id="element-237">
            <div className="flex-shrink-0 mt-0.5" data-id="element-238">
              <span className="flex items-center justify-center h-10 w-10 rounded-full bg-teal-50" data-id="element-239">
                <BadgeCheck className="h-6 w-6 text-teal-600" aria-hidden="true" data-id="element-240" />
              </span>
            </div>

            <div className="min-w-0" data-id="element-241">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-242">
                <span className="text-[11px] font-bold text-teal-600 uppercase tracking-wide" data-id="element-243">
                  Compra verificada
                </span>
              </div>
              <p className="text-sm text-slate-800 leading-snug" data-id="element-244">
                <strong className="font-bold" data-id="element-245">{current.name}</strong> de{' '}
                <strong className="font-semibold" data-id="element-246">{current.city}</strong> compró{' '}
                {current.offer}.
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-247">
                {current.ago} · Pago seguro
              </p>
            </div>

            <button type="button" onClick={handleDismiss} aria-label="Cerrar notificación" className="absolute top-2 right-2 text-slate-300 hover:text-slate-500 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 rounded" data-id="element-248">
              <X className="h-4 w-4" data-id="element-249" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}